import React, { useState, useRef, useEffect } from 'react';
import { Send, Maximize2, Minimize2, X, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/utils/cn';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (imageData?: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function ChatInput({ value, onChange, onSend, placeholder, disabled }: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSendClick = () => {
    if (!value.trim() && !selectedImage) return;
    onSend(selectedImage || undefined);
    setSelectedImage(null);
  };

  const adjustHeight = () => {
    if (textareaRef.current && !isExpanded) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(scrollHeight, 250)}px`;
      
      if (scrollHeight > 250) {
        textareaRef.current.style.overflowY = 'auto';
      } else {
        textareaRef.current.style.overflowY = 'hidden';
      }
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [value, isExpanded]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
      if (isExpanded) setIsExpanded(false);
    }
  };

  const ExpandModal = () => {
    if (!isExpanded) return null;
    
    return (
      <div className="fixed inset-0 z-[10000] bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col animate-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h3 className="font-medium text-slate-800 flex items-center gap-2">
              <Maximize2 size={16} className="text-emerald-500 rotate-90" />
              Chế độ Soạn thảo Mở rộng
            </h3>
            <button 
              onClick={() => setIsExpanded(false)}
              className="p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <div className="flex-1 p-4">
            <textarea
              autoFocus
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="w-full h-full resize-none bg-slate-50 border border-slate-200 rounded-xl p-6 text-sm leading-relaxed focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none"
            />
          </div>
          <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-b-2xl">
            <span className="text-xs font-medium text-slate-400">Shift + Enter để xuống dòng, Enter để Gửi</span>
            <button
              onClick={() => {
                handleSendClick();
                setIsExpanded(false);
              }}
              disabled={disabled || (!value.trim() && !selectedImage)}
              className="px-6 py-2.5 bg-emerald-600 text-white font-medium text-sm rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-all flex items-center gap-2 shadow-lg"
            >
              <Send size={16} />
              Gửi tin nhắn
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="relative group w-full">
        {selectedImage && (
          <div className="relative inline-block mb-3 ml-2 group/img animate-in slide-in-from-bottom-2 fade-in duration-200">
            <img src={selectedImage} alt="Preview" className="h-16 w-16 object-cover rounded-xl border-2 border-emerald-100 shadow-sm" />
            <button 
              onClick={() => setSelectedImage(null)}
              className="absolute -top-2 -right-2 bg-white text-slate-400 rounded-full p-1 border border-slate-200 hover:bg-rose-50 hover:text-rose-500 hover:border-rose-200 shadow-sm transition-all"
            >
              <X size={12} strokeWidth={3} />
            </button>
          </div>
        )}
        <div className="relative w-full bg-slate-50 border-2 border-slate-100 focus-within:border-emerald-500 focus-within:ring-4 focus-within:ring-emerald-500/10 focus-within:bg-white rounded-2xl transition-all shadow-sm flex items-end min-h-[56px]">
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            accept="image/*" 
            className="hidden" 
          />
          <div className="absolute left-3 top-3 flex flex-col items-center gap-2.5 text-slate-400 z-10">
            <button
              onClick={() => setIsExpanded(true)}
              className="hover:text-emerald-600 transition-colors"
              title="Mở rộng khung soạn thảo"
            >
              <Maximize2 size={14} strokeWidth={2.5} className="rotate-90" />
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="hover:text-emerald-600 transition-colors"
              title="Đính kèm hình ảnh"
            >
              <ImageIcon size={16} strokeWidth={2.5} />
            </button>
          </div>

          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            style={{ maxHeight: '250px' }}
            className="w-full bg-transparent py-3.5 pr-12 pl-10 border-0 focus:ring-0 text-sm leading-relaxed focus:outline-none resize-none custom-scrollbar"
          />

          <button
            onClick={handleSendClick}
            disabled={disabled || (!value.trim() && !selectedImage)}
            className="absolute right-2 bottom-2 w-8 h-8 shrink-0 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-all shadow-md active:scale-95 flex items-center justify-center z-10"
          >
            <Send size={14} className="-ml-0.5" />
          </button>
        </div>
      </div>
      
      <ExpandModal />
    </>
  );
}
